import React from 'react';
import { Paper, Typography, Box } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DailyKPI } from '../types';
import { colors, shadows } from '../theme/designSystem';

interface ProductionOverviewChartProps {
  data: DailyKPI[];
}

const ProductionOverviewChart: React.FC<ProductionOverviewChartProps> = ({ data }) => {
  const chartData = data.map((item) => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    gasProduced: parseFloat(Number(item.gas_produced_m3).toFixed(2)),
    electricityUsed: parseFloat(Number(item.electricity_consumed_kwh).toFixed(2)),
    feedstockInput: parseFloat(Number(item.total_feedstock_mass_kg).toFixed(2)),
  })).reverse();

  return (
    <Paper 
      sx={{ 
        p: '24px',
        height: '100%',
        minHeight: '400px',
        borderRadius: '12px',
        border: `1px solid ${colors.neutral[200]}`,
        boxShadow: shadows.card,
      }}
    >
      <Box sx={{ mb: '20px' }}>
        <Typography 
          sx={{ 
            fontSize: '1.25rem',
            fontWeight: 600,
            color: colors.neutral[900],
            mb: 0.5,
          }}
        >
          Production Overview
        </Typography>
        <Typography 
          sx={{ 
            fontSize: '0.875rem',
            color: colors.neutral[500],
          }}
        >
          Last 7 Days Comparison
        </Typography>
      </Box>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[100]} />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12, fill: colors.neutral[500] }}
          />
          <YAxis tick={{ fontSize: 12, fill: colors.neutral[500] }} />
          <Tooltip />
          <Legend />
          <Bar dataKey="gasProduced" fill={colors.primary[600]} name="Gas Produced (m³)" radius={[8, 8, 0, 0]} />
          <Bar dataKey="electricityUsed" fill={colors.status.warning} name="Electricity (kWh)" radius={[8, 8, 0, 0]} />
          <Bar dataKey="feedstockInput" fill={colors.secondary[600]} name="Feedstock (kg)" radius={[8, 8, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default ProductionOverviewChart;
